import React, { useEffect, useState } from "react";
import { api, API_BASE } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { Certificate as CertIcon, Download, ShareNetwork, LinkedinLogo, WhatsappLogo, XLogo, EnvelopeSimple, Link as LinkIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../../components/ui/dropdown-menu";

export default function MyCertificates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/certificates/my").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const verifyUrl = (cert) => `${window.location.origin}/verify/${cert.cert_id}`;

  const copyLink = (cert) => {
    const url = verifyUrl(cert);
    navigator.clipboard.writeText(url);
    toast.success("Verification link copied");
  };

  const shareText = (cert) =>
    `I just earned my "${cert.exam_title}" certificate on EXAM/OS! Verify: ${verifyUrl(cert)}`;

  const shareTargets = (cert) => {
    const url = verifyUrl(cert);
    const title = `EXAM/OS Certificate — ${cert.exam_title}`;
    const text = shareText(cert);
    return {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text)}`,
    };
  };

  const nativeShare = async (cert) => {
    const url = verifyUrl(cert);
    if (navigator.share) {
      try {
        await navigator.share({ title: `EXAM/OS Certificate`, text: shareText(cert), url });
      } catch { /* user cancelled */ }
    } else {
      copyLink(cert);
    }
  };

  return (
    <div>
      <PageHeader
        overline="Achievements"
        title="My certificates"
        subtitle="Download or share your verified credentials."
      />

      {loading ? <div className="text-sm text-zinc-500">Loading…</div> :
        items.length === 0 ? (
          <div className="border border-zinc-200 p-10 text-center text-sm text-zinc-500" data-testid="no-certs">
            No certificates yet. Pass an exam to earn one.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-0 border border-zinc-200" data-testid="my-certificates">
            {items.map((c, i) => {
              const t = shareTargets(c);
              return (
              <div key={c.cert_id} className={`p-6 ${i % 2 === 0 ? "md:border-r" : ""} ${i < items.length - 2 ? "border-b" : i === items.length - 2 && items.length % 2 === 0 ? "border-b" : ""} border-zinc-200`}>
                <div className="flex items-start justify-between mb-4">
                  <CertIcon size={28} weight="regular" className="text-zinc-900" />
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold border border-green-600 text-green-700 bg-green-50 px-2 py-0.5">Verified</div>
                </div>
                <div className="font-display text-xl font-bold mb-1">{c.exam_title}</div>
                <div className="text-xs text-zinc-500 mb-4">Issued {new Date(c.issued_at).toLocaleDateString()}</div>
                <div className="font-mono text-xs text-zinc-700 mb-4">{c.cert_id}</div>
                <div className="text-sm text-zinc-700 mb-4">Score: {c.score}/{c.total_marks} ({c.percentage}%)</div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={`${API_BASE}/certificates/${c.cert_id}/download`}
                    target="_blank"
                    rel="noreferrer"
                    className="bg-zinc-900 text-white px-3 py-2 text-xs font-medium hover:bg-zinc-800 inline-flex items-center gap-1.5"
                    data-testid={`download-cert-${c.cert_id}`}
                  ><Download size={12} /> Download</a>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="border border-zinc-300 px-3 py-2 text-xs font-medium hover:bg-zinc-50 inline-flex items-center gap-1.5"
                        data-testid={`share-cert-${c.cert_id}`}
                      >
                        <ShareNetwork size={12} /> Share
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56">
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Share certificate</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <a
                          href={t.linkedin}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 text-sm cursor-pointer"
                          data-testid={`share-linkedin-${c.cert_id}`}
                        >
                          <LinkedinLogo size={16} weight="fill" className="text-[#0A66C2]" />
                          LinkedIn
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <a
                          href={t.whatsapp}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 text-sm cursor-pointer"
                          data-testid={`share-whatsapp-${c.cert_id}`}
                        >
                          <WhatsappLogo size={16} weight="fill" className="text-[#25D366]" />
                          WhatsApp
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <a
                          href={t.twitter}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 text-sm cursor-pointer"
                          data-testid={`share-twitter-${c.cert_id}`}
                        >
                          <XLogo size={16} weight="regular" className="text-zinc-900" />
                          X (Twitter)
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <a
                          href={t.email}
                          className="flex items-center gap-2 text-sm cursor-pointer"
                          data-testid={`share-email-${c.cert_id}`}
                        >
                          <EnvelopeSimple size={16} weight="regular" className="text-zinc-700" />
                          Email
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => copyLink(c)}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                        data-testid={`share-copy-${c.cert_id}`}
                      >
                        <LinkIcon size={16} weight="regular" className="text-zinc-700" />
                        Copy verification link
                      </DropdownMenuItem>
                      {typeof navigator !== "undefined" && navigator.share && (
                        <DropdownMenuItem
                          onClick={() => nativeShare(c)}
                          className="flex items-center gap-2 text-sm cursor-pointer"
                          data-testid={`share-native-${c.cert_id}`}
                        >
                          <ShareNetwork size={16} weight="regular" className="text-zinc-700" />
                          More…
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
